import React, { useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import CropFreeIcon from '@mui/icons-material/CropFree';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  applyAssetToHtml,
  assemblePreviewHtml,
  extractPreviewPages,
  remoteProjectSignature,
  reviewProject,
  applyPreviewStoreOp,
  loadPreviewStore,
  savePreviewStore,
  clearPreviewStore,
  emptyPreviewStore,
  ProjectFile,
  ProjectState,
  PreviewStore,
} from './previewHtml';
import { PG, pgBtn } from './theme';
import { ConfirmDialog } from './ConfirmDialog';
import { allImageGenModels, allVideoGenModels, suggestedImageModel, suggestedVideoModel } from './helpers';
import { CodeFileEditor, editorLanguage } from './CodeFileEditor';

const HOST_GROUPS: { label: string; options: { id: string; label: string; hint: string }[] }[] = [
  {
    label: 'Not sure',
    options: [
      { id: 'auto', label: 'You pick', hint: 'Full cookbook: static hosts, AWS/GCP/Azure buckets, Cloud Run, Python' },
    ],
  },
  {
    label: 'Static website',
    options: [
      { id: 'netlify', label: 'Netlify', hint: 'Drag-and-drop the unzipped folder' },
      { id: 'github_pages', label: 'GitHub Pages', hint: 'Push the folder, enable Pages on main' },
      { id: 'cloudflare', label: 'Cloudflare Pages', hint: 'Upload assets or connect the repo' },
      { id: 'vercel', label: 'Vercel', hint: 'Import with no build command' },
    ],
  },
  {
    label: 'Cloud buckets',
    options: [
      { id: 's3', label: 'AWS S3 + CloudFront', hint: 'scripts/upload-s3.sh — bucket website, then HTTPS via CloudFront' },
      { id: 'gcs', label: 'Google Cloud Storage', hint: 'scripts/upload-gcs.sh — bucket website or Firebase Hosting' },
      { id: 'azure_blob', label: 'Azure Blob', hint: 'Static website on the $web container' },
    ],
  },
  {
    label: 'App / container',
    options: [
      { id: 'cloud_run', label: 'GCP Cloud Run', hint: 'Dockerfile in the zip, port 8080' },
      { id: 'amplify', label: 'AWS Amplify', hint: 'Connect the repo — amplify.yml, no build' },
      { id: 'docker', label: 'Docker / nginx', hint: 'docker build, then Run, ECS, Fly, or a VM' },
      { id: 'python', label: 'Python web app', hint: 'Flask/FastAPI → Cloud Run, App Engine, Render, Railway' },
      { id: 'static', label: 'Folder / nginx', hint: 'python3 -m http.server or any static root' },
    ],
  },
];

type Props = {
  sessionId: string | null;
  project: ProjectState;
  onSelectPreview?: (path: string) => void;
  onSaveFile: (path: string, content: string) => Promise<void>;
  onDeleteFile: (path: string) => Promise<void>;
  onAddFile?: (path: string, content: string) => Promise<void>;
  onRenameFile?: (from: string, to: string) => Promise<void>;
  onSaveNotes?: (notes: string) => Promise<void>;
  onRestoreFromMessage?: (messageId: string) => Promise<void>;
  restoringId?: string | null;
  onGenerateImage: (path: string, prompt: string, model?: string, selector?: string) => Promise<string | void>;
  onGenerateVideo?: (path: string, prompt: string, model?: string, selector?: string) => Promise<string | void>;
  onUploadAsset?: (file: File) => Promise<{ path: string; url: string } | void>;
  onPreviewChain?: (payload: {
    action: 'execute' | 'poll';
    chainId?: string;
    executionId?: string;
    body?: string;
  }) => Promise<{ status: number; body: any; error?: string }>;
  onPreviewFunction?: (payload: {
    name: string;
    customerId?: string;
    body?: string;
  }) => Promise<{ status: number; body: any; error?: string; runId?: string }>;
  onPreviewNutrition?: (payload: {
    method: string;
    path: string;
    query?: Record<string, string>;
    body?: unknown;
  }) => Promise<{ status: number; body: any; error?: string; requestId?: string }>;
  onPreviewFeedback?: (payload: { batch: boolean; body?: string }) => Promise<{ status: number; body: any; error?: string }>;
  onImportFolder?: (files: FileList) => Promise<void>;
  liveChain?: boolean;
  liveFunction?: boolean;
  liveNutrition?: boolean;
  nutritionSig?: string;
  easyAiBase?: string;
  nutritionBase?: string;
  customerId?: string;
  chainIds?: string[];
  chainNames?: string[];
  onDownloadZip: () => void;
  onExportPack?: (hostTarget: string) => void;
  exportingPack?: boolean;
  onExportCursorPack?: () => void | Promise<void>;
  exportingCursorPack?: boolean;
  onCopyCursorBrief?: () => void;
  generatingImage?: boolean;
  onRecordStudioStep?: (payload: {
    kind: string;
    path?: string;
    prompt?: string;
    selector?: string;
    url?: string;
  }) => Promise<void>;
  messages?: {
    id: string;
    role: string;
    content: string;
    created_at?: string;
    settings_snapshot?: Record<string, any> | null;
  }[];
  onStudioTarget?: (payload: {
    text: string;
    notes: string;
    imageDataUrl?: string | null;
    attachments?: { url: string; filename?: string; kind?: string; content_type?: string }[];
  }) => void | Promise<void>;
  onAskChat?: (text: string) => void;
  canvasHint?: string | null;
  onDismissCanvasHint?: () => void;
  workspace?: 'chat' | 'split' | 'canvas';
};

type PlaceHit = {
  selector: string;
  tag?: string;
  role?: string;
  text?: string;
  className?: string;
  rect?: { x: number; y: number; w: number; h: number; aspect?: number };
  styles?: { background?: string; color?: string; font?: string; radius?: string };
};

function prettyRole(role?: string, tag?: string): string {
  const r = String(role || tag || 'area').toLowerCase();
  const map: Record<string, string> = {
    block: 'Area',
    background: 'Section',
    hero: 'Hero',
    banner: 'Banner',
    button: 'Button',
    heading: 'Heading',
    logo: 'Logo',
    icon: 'Icon',
    image: 'Image',
    video: 'Video',
    input: 'Input',
    nav: 'Nav',
  };
  return map[r] || (r.charAt(0).toUpperCase() + r.slice(1));
}

function shortSelector(sel?: string): string {
  const parts = String(sel || '')
    .split(' > ')
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.slice(-2).join(' › ') || sel || '';
}

function hitChipLabel(h: PlaceHit): string {
  const text = (h.text || '').replace(/\s+/g, ' ').trim().slice(0, 32);
  const size = h.rect ? `${Math.round(h.rect.w)}×${Math.round(h.rect.h)}` : '';
  return [prettyRole(h.role, h.tag), text ? `“${text}”` : '', size].filter(Boolean).join(' · ');
}

function formatStudioSelection(opts: {
  notes?: string;
  hits?: PlaceHit[];
  box?: { x: number; y: number; w: number; h: number };
  pick?: PlaceHit | null;
  previewDead?: boolean;
  issues?: string[];
  previewPath?: string;
  page?: string;
  device?: string;
}): string {
  const lines = ['[Studio selection]'];
  if (opts.previewDead) {
    lines.push(
      'Preview scripts did not respond to Mark — the app is likely broken or blank. Use this region plus any errors below. Do not assume DOM selectors exist.',
    );
  }
  const where = [
    opts.previewPath ? `file ${opts.previewPath}` : '',
    opts.page ? `view ${opts.page}` : '',
    opts.device ? opts.device : '',
  ].filter(Boolean);
  if (where.length) lines.push(`Preview: ${where.join(' · ')}`);
  if (opts.box) {
    lines.push(`Region: ${Math.round(opts.box.w)}×${Math.round(opts.box.h)}px at (${Math.round(opts.box.x)}, ${Math.round(opts.box.y)})`);
  }
  lines.push(
    'A layout sketch of this box is attached (not a photograph). Prefer the labeled parts below over guessing.',
  );
  const hits = opts.hits?.length ? opts.hits : opts.pick ? [opts.pick] : [];
  if (!hits.length && !opts.previewDead) {
    lines.push('No specific DOM parts were detected in the box — change this region using the size and the user notes.');
  }
  hits.slice(0, 8).forEach((h, i) => {
    const r = h.rect;
    const bits = [
      `${i + 1}. ${prettyRole(h.role, h.tag)}${h.tag ? ` <${h.tag}>` : ''}`,
      h.selector || '',
      r ? `${Math.round(r.w)}×${Math.round(r.h)}px` : '',
      h.text ? `“${h.text}”` : '',
      h.className ? `class ${h.className}` : '',
      h.styles?.background ? `bg ${h.styles.background}` : '',
      h.styles?.color ? `color ${h.styles.color}` : '',
      h.styles?.font ? `font ${h.styles.font}` : '',
      h.styles?.radius ? `radius ${h.styles.radius}` : '',
    ].filter(Boolean);
    lines.push(bits.join(' · '));
  });
  lines.push(
    'Edit these elements first (same file / view). Match the stated size, shape, and style. Do not restyle unrelated sections.',
  );
  if (opts.notes?.trim()) {
    lines.push('', opts.notes.trim());
  }
  if (opts.issues?.length) {
    lines.push('', '[Studio preview errors]');
    opts.issues.slice(-6).forEach((m, i) => lines.push(`${i + 1}. ${m}`));
  }
  return lines.join('\n');
}

function jsonSnippet(v: unknown, n = 420): string {
  try {
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    if (!s) return '';
    return s.length > n ? `${s.slice(0, n)}…` : s;
  } catch {
    return String(v ?? '');
  }
}

function formatPreviewFixPrompt(issues: { source: string; message: string }[], extra?: string): string {
  const lines = [
    '[Studio preview errors]',
    'The live Preview failed. Repair the project files (re-emit changed files with path= fences). Use the exact request/response below — do not invent a different route or wrap array bodies in extra keys.',
  ];
  issues.slice(-8).forEach((iss, i) => {
    lines.push(`${i + 1}. (${iss.source}) ${iss.message}`);
  });
  if (extra?.trim()) lines.push('', extra.trim());
  return lines.join('\n');
}

function promptForHit(hit: PlaceHit): { path: string; prompt: string; kind: 'image' | 'video' | 'chat' } {
  const role = (hit.role || '').toLowerCase();
  const w = hit.rect?.w || 0;
  const h = hit.rect?.h || 0;
  const size = w && h ? `${w}×${h}px` : '';
  const style = [hit.styles?.background, hit.styles?.color, hit.styles?.radius].filter(Boolean).join(', ');
  const text = hit.text || '';
  if (role === 'video') {
    return {
      path: 'assets/clip.mp4',
      prompt: `Short looping clip filling a ${size || 'hero'} video region. ${text} ${style}`.trim(),
      kind: 'video',
    };
  }
  if (role === 'logo') {
    return {
      path: 'assets/logo.png',
      prompt: `App logo, ${size || 'compact'}, object-fit contain, matching ${style || 'the page palette'}. No tiny unreadable text.`,
      kind: 'image',
    };
  }
  if (role === 'icon') {
    return {
      path: 'assets/icon.png',
      prompt: `Simple UI icon, ${size || '32×32'}, flat, matching ${style || 'the page'}.`,
      kind: 'image',
    };
  }
  if (role === 'hero' || role === 'background') {
    return {
      path: 'assets/hero.png',
      prompt: `Editorial photo filling ${size || 'a wide hero'}, ${style}. No overlay text.`,
      kind: 'image',
    };
  }
  if (role === 'button' || role === 'heading') {
    return { path: 'assets/placed.png', prompt: text, kind: 'chat' };
  }
  return {
    path: w <= 180 ? 'assets/logo.png' : 'assets/placed.png',
    prompt: `Image for a ${role || hit.tag || 'region'} ${size}. ${text} ${style}`.trim(),
    kind: 'image',
  };
}

export const AppBuilderPane: React.FC<Props> = ({
  sessionId,
  project,
  onSelectPreview,
  onSaveFile,
  onDeleteFile,
  onAddFile,
  onRenameFile,
  onSaveNotes,
  onRestoreFromMessage,
  restoringId,
  onGenerateImage,
  onGenerateVideo,
  onUploadAsset,
  onPreviewChain,
  onPreviewFunction,
  onPreviewNutrition,
  onPreviewFeedback,
  onImportFolder,
  liveChain,
  liveFunction,
  liveNutrition,
  nutritionSig,
  easyAiBase,
  nutritionBase,
  customerId,
  chainIds,
  chainNames,
  onDownloadZip,
  onExportPack,
  exportingPack,
  onExportCursorPack,
  exportingCursorPack,
  onCopyCursorBrief,
  generatingImage,
  onRecordStudioStep,
  messages,
  onStudioTarget,
  onAskChat,
  canvasHint,
  onDismissCanvasHint,
  workspace = 'split',
}) => {
  const files = project.files || {};
  const paths = Object.keys(files).sort();
  const [tab, setTab] = useState(0);
  const [activePath, setActivePath] = useState<string>(project.preview_path || paths[0] || 'index.html');
  const [draft, setDraft] = useState('');
  const [notes, setNotes] = useState(project.notes || '');
  const [imgOpen, setImgOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deletePath, setDeletePath] = useState<string | null>(null);
  const [newPath, setNewPath] = useState('notes.md');
  const [renameTo, setRenameTo] = useState('');
  const [imgPath, setImgPath] = useState('assets/hero.png');
  const [imgPrompt, setImgPrompt] = useState(
    'Soft editorial photo of whole foods on linen, cream and sage, no text, 4:5',
  );
  const [imgModel, setImgModel] = useState(suggestedImageModel);
  const [vidModel, setVidModel] = useState(suggestedVideoModel);
  const [previewKey, setPreviewKey] = useState(0);
  const [clearDataOpen, setClearDataOpen] = useState(false);
  const [noteSaveState, setNoteSaveState] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadBatch, setUploadBatch] = useState<
    { path: string; url: string; kind: string; name: string; contentType?: string }[]
  >([]);
  const [device, setDevice] = useState<'phone' | 'tablet' | 'full'>('full');
  const [saveState, setSaveState] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [cursorOpen, setCursorOpen] = useState(false);
  const [hostTarget, setHostTarget] = useState('auto');
  const [pageId, setPageId] = useState('');
  const [pickMode, setPickMode] = useState(false);
  const [regionMode, setRegionMode] = useState(false);
  const [placeSelector, setPlaceSelector] = useState('');
  const [placeHit, setPlaceHit] = useState<PlaceHit | null>(null);
  const placeHitRef = useRef<PlaceHit | null>(null);
  const [placeSheetOpen, setPlaceSheetOpen] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  const [markSending, setMarkSending] = useState(false);
  const [markNotes, setMarkNotes] = useState('');
  const [markHits, setMarkHits] = useState<PlaceHit[]>([]);
  const [markBox, setMarkBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [markImage, setMarkImage] = useState<string | null>(null);
  const [vidOpen, setVidOpen] = useState(false);
  const [vidPath, setVidPath] = useState('assets/clip.mp4');
  const [vidPrompt, setVidPrompt] = useState(
    'Soft 8-second product clip, cream kitchen light, no text, 9:16 then crop',
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previewStoreRef = useRef<PreviewStore>(loadPreviewStore(sessionId));
  const assetInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [previewIssues, setPreviewIssues] = useState<{ at: number; source: string; message: string }[]>([]);
  const [previewAlive, setPreviewAlive] = useState(false);
  const [markDead, setMarkDead] = useState(false);
  const [overlayBox, setOverlayBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const pendingMarkRef = useRef<number | null>(null);
  const overlayDragRef = useRef<{ x: number; y: number } | null>(null);
  const [toolsOpen, setToolsOpen] = useState(() => {
    try {
      return window.localStorage.getItem('easyai-preview-tools') !== '0';
    } catch {
      return true;
    }
  });

  const pushIssue = (source: string, message: string) => {
    const msg = String(message || '').trim();
    if (!msg) return;
    setPreviewErr(msg);
    setPreviewIssues((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.message === msg) return prev;
      return [...prev, { at: Date.now(), source, message: msg }].slice(-12);
    });
  };

  const active = files[activePath];

  React.useEffect(() => {
    if (activePath && files[activePath]) {
      setDraft(files[activePath].content || '');
    } else if (paths[0]) {
      setActivePath(paths[0]);
      setDraft(files[paths[0]]?.content || '');
    } else {
      setActivePath('');
      setDraft('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath, files[activePath]?.content, files[activePath]?.url, paths.join('|')]);

  React.useEffect(() => {
    setNotes(project.notes || '');
  }, [project.notes]);

  React.useEffect(() => {
    previewStoreRef.current = loadPreviewStore(sessionId);
  }, [sessionId]);

  const skipWorkspaceReload = useRef(true);
  React.useLayoutEffect(() => {
    if (skipWorkspaceReload.current) {
      skipWorkspaceReload.current = false;
      return;
    }
    setTab(0);
    setPickMode(false);
    setRegionMode(false);
    setPlaceSheetOpen(false);
    setMarkOpen(false);
    if (workspace !== 'chat') {
      setPreviewKey((k) => k + 1);
    }
  }, [workspace]);

  const remoteSig = useMemo(() => remoteProjectSignature(project), [project]);
  const skipFirstSig = useRef(true);
  const lastNutr = useRef<string | undefined>(undefined);

  React.useEffect(() => {
    if (skipFirstSig.current) {
      skipFirstSig.current = false;
      return;
    }
    setPreviewKey((k) => k + 1);
  }, [remoteSig]);

  React.useEffect(() => {
    if (lastNutr.current === undefined) {
      lastNutr.current = nutritionSig || '';
      return;
    }
    if ((nutritionSig || '') === lastNutr.current) return;
    lastNutr.current = nutritionSig || '';
    setPreviewKey((k) => k + 1);
  }, [nutritionSig]);

  React.useEffect(() => {
    if (!activePath || !sessionId) return;
    if (!files[activePath]) return;
    const saved = files[activePath]?.content || '';
    if (draft === saved) return;
    const t = window.setTimeout(async () => {
      await onSaveFile(activePath, draft);
      setSaveState('Saved');
      setTimeout(() => setSaveState(''), 1200);
    }, 700);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, activePath, sessionId]);

  const liveProject = useMemo(() => {
    const current = project.files || {};
    if (!activePath || !current[activePath]) return project;
    return {
      ...project,
      files: {
        ...current,
        [activePath]: { ...current[activePath], content: draft, path: activePath },
      },
    };
  }, [project, activePath, draft]);

  const pages = useMemo(() => extractPreviewPages(liveProject), [liveProject]);

  const srcDoc = useMemo(
    () =>
      assemblePreviewHtml(liveProject, {
        liveChain: !!liveChain,
        liveFunction: !!liveFunction,
        liveNutrition: !!liveNutrition,
        easyAiBase,
        nutritionBase,
        customerId,
        chainIds,
        previewStore: previewStoreRef.current,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previewKey re-reads previewStoreRef on remount
    [liveProject, liveChain, liveFunction, liveNutrition, easyAiBase, nutritionBase, customerId, chainIds, previewKey],
  );
  const review = useMemo(() => reviewProject(liveProject), [liveProject]);
  const imageModels = useMemo(() => allImageGenModels(), []);
  const videoModels = useMemo(() => allVideoGenModels(), []);
  const frameWidth = device === 'phone' ? 390 : device === 'tablet' ? 768 : '100%';

  const htmlPreviewPath = project.preview_path || paths.find((p) => p.toLowerCase().endsWith('.html')) || '';

  const timeline = useMemo(() => {
    const byId = new Map<
      string,
      {
        key: string;
        at?: string;
        source: string;
        text: string;
        message_id?: string;
        files_changed?: string[];
      }
    >();
    (project.notes_log || []).forEach((n, i) => {
      const key = n.message_id || `note-${n.at || i}-${(n.text || '').slice(0, 24)}`;
      byId.set(key, {
        key,
        at: n.at,
        source: n.source || 'user',
        text: n.text || '',
        message_id: n.message_id,
        files_changed: n.files_changed,
      });
    });
    (messages || []).forEach((m) => {
      if (m.role !== 'assistant') return;
      const snap = m.settings_snapshot;
      const files = snap?.project?.files;
      const hasFiles = files && typeof files === 'object' && Object.keys(files).length > 0;
      if (!hasFiles && !snap?.studio_step) return;
      if (byId.has(m.id)) return;
      byId.set(m.id, {
        key: m.id,
        at: m.created_at,
        source: snap?.studio_step ? 'studio' : 'assistant',
        text: (m.content || '').replace(/```[\s\S]*?```/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 400),
        message_id: m.id,
        files_changed: snap?.project?.review?.files_changed,
      });
    });
    return Array.from(byId.values()).sort((a, b) => (a.at || '').localeCompare(b.at || ''));
  }, [project.notes_log, messages]);

  const postToPreview = (data: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(data, '*');
  };

  const openMarkFromOverlay = (box: { x: number; y: number; w: number; h: number }) => {
    postToPreview({ type: 'easyai-region-query', box });
    if (pendingMarkRef.current) window.clearTimeout(pendingMarkRef.current);
    pendingMarkRef.current = window.setTimeout(() => {
      pendingMarkRef.current = null;
      setRegionMode(false);
      setOverlayBox(null);
      overlayDragRef.current = null;
      setMarkHits([]);
      setMarkBox(box);
      setMarkImage(null);
      setMarkDead(true);
      setMarkNotes('');
      setMarkOpen(true);
    }, previewAlive ? 280 : 60);
  };

  React.useEffect(() => {
    const onMsg = async (ev: MessageEvent) => {
      const d = ev.data;
      if (!d || typeof d !== 'object') return;
      if (iframeRef.current && ev.source && ev.source !== iframeRef.current.contentWindow) return;
      if (d.type === 'easyai-pick' && d.selector) {
        setPickMode(false);
        postToPreview({ type: 'easyai-set-pick', on: false });
        const hit: PlaceHit = {
          selector: String(d.selector),
          tag: d.tag ? String(d.tag).toLowerCase() : undefined,
          role: d.role ? String(d.role) : undefined,
          text: d.text ? String(d.text) : undefined,
          className: d.className ? String(d.className) : undefined,
          rect: d.rect && typeof d.rect === 'object' ? d.rect : undefined,
          styles: d.styles && typeof d.styles === 'object' ? d.styles : undefined,
        };
        placeHitRef.current = hit;
        setPlaceHit(hit);
        setPlaceSelector(hit.selector);
        const guessed = promptForHit(hit);
        if (guessed.kind === 'video') {
          setVidPath(guessed.path);
          setVidPrompt(guessed.prompt);
        } else {
          setImgPath(guessed.path);
          if (guessed.prompt) setImgPrompt(guessed.prompt);
        }
        setPlaceSheetOpen(true);
      }
      if (d.type === 'easyai-preview-ready') {
        setPreviewAlive(true);
      }
      if (d.type === 'easyai-region') {
        if (pendingMarkRef.current) {
          window.clearTimeout(pendingMarkRef.current);
          pendingMarkRef.current = null;
        }
        setRegionMode(false);
        setOverlayBox(null);
        overlayDragRef.current = null;
        postToPreview({ type: 'easyai-set-region', on: false });
        const hits = Array.isArray(d.hits)
          ? (d.hits as PlaceHit[]).filter((h) => h?.selector)
          : [];
        setMarkHits(hits);
        setMarkBox(d.box && typeof d.box === 'object' ? d.box : null);
        setMarkImage(typeof d.image === 'string' ? d.image : null);
        setMarkDead(!hits.length && !d.image);
        setMarkNotes('');
        if (hits[0]) {
          placeHitRef.current = hits[0];
          setPlaceHit(hits[0]);
          setPlaceSelector(hits[0].selector);
        }
        setMarkOpen(true);
      }
      if (d.type === 'easyai-preview-error' && d.message) {
        pushIssue('js', String(d.message));
      }
      if (d.type === 'easyai-chain-proxy' && onPreviewChain) {
        let inputBody = d.body;
        if (typeof inputBody !== 'string' && inputBody != null) {
          try {
            inputBody = JSON.stringify(inputBody);
          } catch {
            inputBody = '';
          }
        }
        const uuid =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        let chainId = String(d.chainId || '');
        const allowed = (chainIds || []).filter((id) => uuid.test(id));
        if (d.action !== 'poll') {
          const known = allowed.map((id) => id.toLowerCase());
          if (!chainId || !uuid.test(chainId) || (known.length > 0 && !known.includes(chainId.toLowerCase()))) {
            chainId = allowed[0] || chainId;
          }
        }
        if (d.action !== 'poll') {
          setPreviewErr(null);
        }
        const result = await onPreviewChain({
          action: d.action === 'poll' ? 'poll' : 'execute',
          chainId,
          executionId: d.executionId,
          body: inputBody,
        });
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'easyai-chain-proxy-result', id: d.id, ...result },
          '*',
        );
        if (result.error) {
          pushIssue('chain', result.error);
        } else if (result.body?.status === 'failed' || result.body?.status === 'cancelled') {
          pushIssue('chain', String(result.body?.error || `Chain ${result.body.status}`));
        }
      }
      if (d.type === 'easyai-fn-proxy' && onPreviewFunction) {
        let inputBody = d.body;
        if (typeof inputBody !== 'string' && inputBody != null) {
          try {
            inputBody = JSON.stringify(inputBody);
          } catch {
            inputBody = '';
          }
        }
        const result = await onPreviewFunction({
          name: d.name,
          customerId: d.customerId,
          body: inputBody,
        });
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'easyai-fn-proxy-result', id: d.id, ...result },
          '*',
        );
        if (result.error) {
          pushIssue('function', result.error);
        } else if (result.status && result.status >= 400) {
          pushIssue(
            'function',
            `Function ${result.status} ${d.name || ''}` +
              (result.body ? `\nResponse: ${jsonSnippet(result.body, 360)}` : ''),
          );
        }
      }
      if (d.type === 'easyai-nutrition-proxy' && onPreviewNutrition) {
        const result = await onPreviewNutrition({
          method: String(d.method || 'GET'),
          path: String(d.path || ''),
          query: d.query && typeof d.query === 'object' ? d.query : {},
          body: d.body,
        });
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'easyai-nutrition-proxy-result', id: d.id, ...result },
          '*',
        );
        if (result.error || (result.status && result.status >= 400)) {
          const req = jsonSnippet(d.body, 280);
          const resBody = jsonSnippet(result.body, 360);
          pushIssue(
            'nutrition',
            `NutritionAI ${result.status || 400} ${String(d.method || 'GET').toUpperCase()} ${d.path || ''}`
              + (req ? `\nRequest: ${req}` : '')
              + (resBody ? `\nResponse: ${resBody}` : '')
              + (result.error && !resBody ? `\n${result.error}` : ''),
          );
        }
      }
      if (d.type === 'easyai-feedback-proxy' && onPreviewFeedback) {
        const result = await onPreviewFeedback({
          batch: !!d.batch,
          body: typeof d.body === 'string' ? d.body : JSON.stringify(d.body ?? {}),
        });
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'easyai-feedback-proxy-result', id: d.id, ...result },
          '*',
        );
        if (result.error) {
          pushIssue('feedback', `Feedback ${result.status}: ${result.error}`);
        }
      }
      if (d.type === 'easyai-storage-sync') {
        previewStoreRef.current = applyPreviewStoreOp(previewStoreRef.current, d);
        savePreviewStore(sessionId, previewStoreRef.current);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [onPreviewChain, onPreviewFunction, onPreviewNutrition, onPreviewFeedback, chainIds, sessionId]);

  React.useEffect(() => {
    setPreviewAlive(false);
  }, [previewKey]);

  React.useEffect(() => {
    postToPreview({ type: 'easyai-set-pick', on: pickMode });
  }, [pickMode, previewKey]);

  React.useEffect(() => {
    postToPreview({ type: 'easyai-set-region', on: regionMode });
  }, [regionMode, previewKey]);

  React.useEffect(() => {
    if (!pickMode && !regionMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setPickMode(false);
      setRegionMode(false);
      postToPreview({ type: 'easyai-set-pick', on: false });
      postToPreview({ type: 'easyai-set-region', on: false });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickMode, regionMode]);

  const placeOnHtml = async (url: string, kind: 'image' | 'video') => {
    if (!placeSelector || !htmlPreviewPath) {
      setPlaceSelector('');
      return;
    }
    const current =
      htmlPreviewPath === activePath ? draft : files[htmlPreviewPath]?.content || '';
    if (!current) {
      setPlaceSelector('');
      return;
    }
    const hit = placeHitRef.current;
    const next = applyAssetToHtml(current, placeSelector, url, kind, {
      width: hit?.rect?.w,
      height: hit?.rect?.h,
      role: hit?.role || (kind === 'video' ? 'video' : undefined),
    });
    if (htmlPreviewPath === activePath) setDraft(next);
    await onSaveFile(htmlPreviewPath, next);
    setPlaceSelector('');
    setPreviewKey((k) => k + 1);
  };

  const selectFile = (path: string) => {
    setActivePath(path);
    setDraft(files[path]?.content || '');
    if (path.endsWith('.html')) onSelectPreview?.(path);
  };

  const save = async () => {
    if (!activePath) return;
    await onSaveFile(activePath, draft);
  };

  const saveAndRefreshPreview = async () => {
    if (!activePath) return;
    const saved = files[activePath]?.content || '';
    if (draft !== saved) {
      await onSaveFile(activePath, draft);
    }
    setSaveState('Saved');
    window.setTimeout(() => setSaveState(''), 1200);
    setPreviewKey((k) => k + 1);
    setTab(0);
  };

  const dirty = !!activePath && draft !== (files[activePath]?.content || '');

  const commitNote = async () => {
    if (!notes.trim() || savingNote || !onSaveNotes) return;
    setSavingNote(true);
    try {
      await onSaveNotes(notes);
      setNoteSaveState('Saved to timeline');
      window.setTimeout(() => setNoteSaveState(''), 1800);
    } finally {
      setSavingNote(false);
    }
  };

  const sendMarkToChat = () => {
    if (!onStudioTarget || !markNotes.trim() || markSending) return;
    const page = pages.find((p) => p.id === (pageId || pages[0]?.id));
    const pageLabel =
      page?.kind === 'file'
        ? page.path || page.label
        : page && page.id !== 'all'
          ? `${page.label} (data-page="${page.id}")`
          : '';
    const text = formatStudioSelection({
      notes: markNotes,
      hits: markHits.length ? markHits : placeHit ? [placeHit] : [],
      box: markBox || undefined,
      pick: placeHit,
      previewDead: markDead,
      issues: previewIssues.map((x) => x.message),
      previewPath: htmlPreviewPath || undefined,
      page: pageLabel || undefined,
      device: device === 'phone' ? '390px phone' : device === 'tablet' ? '768px tablet' : 'full pane',
    });
    const payload = { text, notes: markNotes, imageDataUrl: markImage };
    setMarkSending(true);
    setMarkOpen(false);
    setPlaceSheetOpen(false);
    setMarkNotes('');
    setRegionMode(false);
    void Promise.resolve(onStudioTarget(payload)).finally(() => setMarkSending(false));
  };

  const sendUploadToChat = async () => {
    if (!onStudioTarget || !uploadBatch.length) return;
    const lines = [
      '[Studio assets]',
      'These files are already in the project. Use the URLs in <img src> (and CSS). Do not re-host.',
      ...uploadBatch.map((a) => `- \`${a.path}\` (${a.kind}) ${a.url}`),
    ];
    const extra = uploadNotes.trim();
    if (extra) lines.push(extra);
    const payload = {
      text: lines.join('\n'),
      notes: extra,
      attachments: uploadBatch.map((a) => ({
        url: a.url,
        filename: a.name || a.path.split('/').pop(),
        kind: a.kind,
        content_type: a.contentType,
      })),
    };
    setUploadOpen(false);
    setUploadBatch([]);
    setUploadNotes('');
    void Promise.resolve(onStudioTarget(payload));
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%', color: PG.text, pl: 1 }}>
      <Stack spacing={0.75} sx={{ mb: 1.25 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        sx={{
          flexWrap: 'wrap',
          rowGap: 0.75,
          minHeight: 40,
        }}
      >
        <Tabs
          key={workspace}
          value={tab}
          variant="standard"
          onChange={(_, v) => {
            setTab(v);
            if (v === 0) setPreviewKey((k) => k + 1);
          }}
          sx={{ minHeight: 36, minWidth: 0, '& .MuiTab-root': { color: PG.muted, minHeight: 36, px: 1.25, whiteSpace: 'nowrap' }, '& .Mui-selected': { color: PG.text } }}
        >
          <Tab label="Preview" sx={{ minHeight: 36 }} title="Live app. Submit the form to run an attached chain." />
          <Tab label="Code" sx={{ minHeight: 36 }} title="Edit files. Save writes the file and reloads Preview." />
          <Tab label="Review" sx={{ minHeight: 36 }} title="Checklist: pages, README, hosting, source files." />
          <Tab label="Notes" sx={{ minHeight: 36 }} title="Scratch notes for this project." />
        </Tabs>
        {tab === 0 && (
          <>
            <Tooltip title="Preview width: phone (390), tablet (768), or full pane. Does not change the generated CSS.">
              <ToggleButtonGroup exclusive size="small" value={device} onChange={(_, v) => v && setDevice(v)}>
                <ToggleButton value="phone" sx={{ px: 1.25 }}>390</ToggleButton>
                <ToggleButton value="tablet" sx={{ px: 1.25 }}>768</ToggleButton>
                <ToggleButton value="full" sx={{ px: 1.25 }}>Full</ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
            <Tooltip title="Jump between views. Input = the form, Waiting = progress while the chain runs, Results = parsed output.">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ color: PG.muted }}>Page</InputLabel>
                <Select
                  label="Page"
                  value={pageId || pages[0]?.id || ''}
                  onChange={(e) => {
                    const id = String(e.target.value);
                    setPageId(id);
                    const page = pages.find((p) => p.id === id);
                    if (page?.kind === 'file' && page.path) {
                      onSelectPreview?.(page.path);
                      setPreviewKey((k) => k + 1);
                    } else {
                      postToPreview({ type: 'easyai-show-page', page: id });
                    }
                  }}
                  sx={{ color: PG.text, '.MuiOutlinedInput-notchedOutline': { borderColor: PG.border } }}
                >
                  {pages.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          </>
        )}
        <Tooltip title="Reload the preview iframe if the form looks frozen or a run got stuck.">
          <IconButton size="small" onClick={() => setPreviewKey((k) => k + 1)} sx={{ color: PG.muted }} aria-label="Reload preview">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Wipe this app’s saved Preview data (food log, localStorage). Files stay. Use after a rebuild that changed how the app stores data.">
          <IconButton
            size="small"
            onClick={() => setClearDataOpen(true)}
            disabled={!sessionId}
            sx={{ color: PG.muted }}
            aria-label="Clear preview data"
          >
            <DeleteSweepIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open this same preview HTML in a new browser tab.">
          <IconButton
            size="small"
            sx={{ color: PG.muted }}
            aria-label="Open preview in a new tab"
            onClick={() => {
              const w = window.open('', 'easyai-preview');
              if (!w) return;
              w.document.open();
              w.document.write(srcDoc);
              w.document.close();
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <Tooltip title={toolsOpen ? 'Hide Place, Mark, export — more room for Preview' : 'Show Place, Mark, export'}>
          <IconButton
            size="small"
            onClick={() => {
              const next = !toolsOpen;
              setToolsOpen(next);
              try {
                window.localStorage.setItem('easyai-preview-tools', next ? '1' : '0');
              } catch {
                /* ignore */
              }
            }}
            sx={{ color: PG.muted }}
            aria-label={toolsOpen ? 'Hide preview tools' : 'Show preview tools'}
          >
            {toolsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
      {(toolsOpen || pickMode || regionMode) && (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
      >
        <Tooltip title="Add a blank file to the project (HTML, CSS, JS, README, Python, …).">
          <span>
            <Button
              size="small"
              startIcon={<NoteAddIcon />}
              disabled={!sessionId}
              onClick={() => setAddOpen(true)}
              sx={{ ...pgBtn, color: PG.text }}
            >
              Add file
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Generate a still image (logo, hero, product) into assets/. Use Place first to drop it on a specific region of Preview.">
          <span>
            <Button
              size="small"
              startIcon={<ImageIcon />}
              disabled={!sessionId}
              onClick={() => {
                setPlaceSelector('');
                setPlaceHit(null);
                placeHitRef.current = null;
                setImgOpen(true);
              }}
              sx={{ ...pgBtn, color: PG.text }}
            >
              Image
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Generate a short looping video with Veo into assets/. Uses credits. Place first to insert it into a clicked region.">
          <span>
            <Button
              size="small"
              startIcon={<MovieIcon />}
              disabled={!sessionId || !onGenerateVideo}
              onClick={() => {
                setPlaceSelector('');
                setPlaceHit(null);
                placeHitRef.current = null;
                setVidOpen(true);
              }}
              sx={{ ...pgBtn, color: PG.text }}
            >
              Video
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Add your logo or photos to assets/. Then say where they go (header logo, hero). Place first if you want them dropped on a clicked spot.">
          <span>
            <Button
              size="small"
              startIcon={uploadBusy ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />}
              disabled={!sessionId || !onUploadAsset || uploadBusy}
              onClick={() => assetInputRef.current?.click()}
              sx={{ ...pgBtn, color: PG.text }}
            >
              {uploadBusy ? 'Uploading…' : 'Upload'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Import an existing app or project folder (skips node_modules). Then describe where the chain should go — we match that project’s layout instead of starting from scratch.">
          <span>
            <Button
              size="small"
              startIcon={<FolderOpenIcon />}
              disabled={!sessionId || !onImportFolder}
              onClick={() => {
                const el = folderInputRef.current;
                if (!el) return;
                el.setAttribute('webkitdirectory', 'true');
                el.click();
              }}
              sx={{ ...pgBtn, color: PG.text }}
            >
              Import project
            </Button>
          </span>
        </Tooltip>
        <input
          ref={folderInputRef}
          type="file"
          hidden
          multiple
          onChange={async (e) => {
            const list = e.target.files;
            e.target.value = '';
            if (list?.length && onImportFolder) await onImportFolder(list);
          }}
        />
        <Tooltip title="Click Place, then click a logo, banner, heading, or image in Preview. We infer size and role, then you can generate into that spot or describe the change in chat.">
          <span>
            <Button
              size="small"
              startIcon={<AdsClickIcon />}
              disabled={!sessionId}
              variant={pickMode ? 'contained' : 'text'}
              onClick={() => {
                setRegionMode(false);
                setPickMode((v) => !v);
              }}
              sx={{ ...pgBtn, color: pickMode ? '#0b1220' : PG.text }}
            >
              {pickMode ? 'Click preview…' : 'Place'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Drag a box on Preview. Chat gets a layout sketch, the current page/file, labeled parts, and your notes — even if the app is broken.">
          <span>
            <Button
              size="small"
              startIcon={<CropFreeIcon />}
              disabled={!sessionId || !onStudioTarget}
              variant={regionMode ? 'contained' : 'text'}
              onClick={() => {
                setPickMode(false);
                setRegionMode((v) => !v);
              }}
              sx={{ ...pgBtn, color: regionMode ? '#0b1220' : PG.text }}
            >
              {regionMode ? 'Drag on preview…' : 'Mark'}
            </Button>
          </span>
        </Tooltip>
        {(pickMode || regionMode) && (
          <Typography variant="caption" sx={{ color: PG.accent, fontWeight: 700, whiteSpace: 'nowrap' }}>
            Esc cancels
          </Typography>
        )}
        <input
          ref={assetInputRef}
          type="file"
          accept="image/*,video/*"
          hidden
          multiple
          onChange={async (e) => {
            const list = Array.from(e.target.files || []);
            e.target.value = '';
            if (!list.length || !onUploadAsset) return;
            const sel = placeSelector;
            setUploadBusy(true);
            const uploaded: {
              path: string;
              url: string;
              kind: string;
              name: string;
              contentType?: string;
            }[] = [];
            try {
              for (const file of list) {
                const rec = await onUploadAsset(file);
                if (!rec) continue;
                const kind = file.type.startsWith('video/') ? 'video' : 'image';
                if (rec.url && sel) await placeOnHtml(rec.url, kind);
                if (onRecordStudioStep) {
                  await onRecordStudioStep({
                    kind: 'upload',
                    path: rec.path,
                    url: rec.url,
                    selector: sel || undefined,
                  });
                }
                uploaded.push({
                  path: rec.path,
                  url: rec.url,
                  kind,
                  name: file.name,
                  contentType: file.type,
                });
              }
            } finally {
              setUploadBusy(false);
            }
            if (!sel && uploaded.length) {
              setUploadBatch(uploaded);
              setUploadNotes('');
              setUploadOpen(true);
            }
          }}
        />
        <Tooltip title="Zip with START_HERE.md, HOSTING.md, env examples, and deploy files. Unzip, paste your EasyAI API key, follow START_HERE. The zip calls the attached chain — it does not recreate it.">
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={exportingPack ? <CircularProgress size={14} color="inherit" /> : <IosShareIcon />}
              disabled={!sessionId || paths.length === 0 || !onExportPack || exportingPack}
              onClick={() => setExportOpen(true)}
              sx={{ ...pgBtn, fontWeight: 700, bgcolor: PG.accent, color: '#0b1220', '&:hover': { bgcolor: '#7dd3fc' } }}
            >
              {exportingPack ? 'Exporting…' : 'Export for hosting'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Zip Cursor can File → Open Folder: START_HERE.md, EasyAI docs, attached chains/functions, and your current app files. Build in your own environment without Studio.">
          <span>
            <Button
              size="small"
              startIcon={exportingCursorPack ? <CircularProgress size={14} color="inherit" /> : <IntegrationInstructionsIcon />}
              disabled={!sessionId || !onExportCursorPack || exportingCursorPack}
              onClick={() => setCursorOpen(true)}
              sx={{
                ...pgBtn,
                fontWeight: 800,
                color: '#f5f3ff',
                bgcolor: '#5b4dff',
                boxShadow: '0 0 0 2px rgba(167,139,250,0.35)',
                '&:hover': { bgcolor: '#6d5cff' },
              }}
            >
              {exportingCursorPack ? 'Packing…' : 'Cursor pack'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Raw zip of current files only, no hosting cookbook. Prefer Export for hosting when handing this off.">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!sessionId || paths.length === 0}
              onClick={onDownloadZip}
              sx={{ ...pgBtn, color: PG.text, borderColor: PG.border }}
            >
              Download zip
            </Button>
          </span>
        </Tooltip>
      </Stack>
      )}
      </Stack>
      {exportingPack && (
        <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 1, py: 0 }}>
          Writing README, start scripts, hosting notes, and deploy files — then downloading a zip. Unzip and open HOSTING.md.
        </Alert>
      )}
      {exportingCursorPack && (
        <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 1, py: 0 }}>
          Building the Cursor pack — START_HERE.md, EasyAI docs, attached chains, then download.
        </Alert>
      )}

      {tab === 0 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            bgcolor: PG.preview,
            borderRadius: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            p: 1,
          }}
        >
          <Box
            sx={{
              width: frameWidth,
              maxWidth: '100%',
              height: '100%',
              minHeight: 0,
              borderRadius: device === 'full' ? 1 : '28px',
              overflow: 'hidden',
              border: device === 'full' ? '1px solid #243044' : '10px solid #020617',
              bgcolor: '#fff',
              boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
              position: 'relative',
            }}
          >
            <iframe
              ref={iframeRef}
              key={previewKey}
              title="App preview"
              sandbox="allow-scripts allow-forms allow-modals allow-popups"
              srcDoc={srcDoc}
              onLoad={() => {
                if (pageId && !pageId.startsWith('file:')) {
                  iframeRef.current?.contentWindow?.postMessage({ type: 'easyai-show-page', page: pageId }, '*');
                }
                if (pickMode) {
                  iframeRef.current?.contentWindow?.postMessage({ type: 'easyai-set-pick', on: true }, '*');
                }
                if (regionMode) {
                  iframeRef.current?.contentWindow?.postMessage({ type: 'easyai-set-region', on: true }, '*');
                }
              }}
              style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
            />
            {regionMode && (
              <Box
                onPointerDown={(e) => {
                  if (e.button != null && e.button !== 0) return;
                  e.preventDefault();
                  e.currentTarget.setPointerCapture(e.pointerId);
                  const rec = e.currentTarget.getBoundingClientRect();
                  const pt = { x: e.clientX - rec.left, y: e.clientY - rec.top };
                  overlayDragRef.current = pt;
                  setOverlayBox({ ...pt, w: 0, h: 0 });
                }}
                onPointerMove={(e) => {
                  const start = overlayDragRef.current;
                  if (!start) return;
                  const rec = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rec.left;
                  const y = e.clientY - rec.top;
                  setOverlayBox({
                    x: Math.min(x, start.x),
                    y: Math.min(y, start.y),
                    w: Math.abs(x - start.x),
                    h: Math.abs(y - start.y),
                  });
                }}
                onPointerUp={(e) => {
                  const start = overlayDragRef.current;
                  overlayDragRef.current = null;
                  try {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                  } catch {
                    /* already released */
                  }
                  if (!start) return;
                  const rec = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rec.left;
                  const y = e.clientY - rec.top;
                  const box = {
                    x: Math.min(x, start.x),
                    y: Math.min(y, start.y),
                    w: Math.max(8, Math.abs(x - start.x)),
                    h: Math.max(8, Math.abs(y - start.y)),
                  };
                  setOverlayBox(null);
                  openMarkFromOverlay(box);
                }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 4,
                  cursor: 'crosshair',
                  touchAction: 'none',
                  bgcolor: previewAlive ? 'transparent' : 'rgba(15,23,42,0.04)',
                }}
              >
                {overlayBox && overlayBox.w + overlayBox.h > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: overlayBox.x,
                      top: overlayBox.y,
                      width: overlayBox.w,
                      height: overlayBox.h,
                      border: '2px dashed #38bdf8',
                      bgcolor: 'rgba(56,189,248,0.12)',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
          {(previewErr || canvasHint || previewIssues.length > 0) && (
            <Stack
              spacing={0.75}
              sx={{
                position: 'absolute',
                left: 12,
                right: 12,
                bottom: 12,
                zIndex: 5,
                pointerEvents: 'none',
                alignItems: 'stretch',
                maxWidth: 560,
                mx: 'auto',
              }}
            >
              {previewErr && (
                <Alert
                  severity="error"
                  onClose={() => setPreviewErr(null)}
                  sx={{ pointerEvents: 'auto', py: 0.25, boxShadow: '0 8px 24px rgba(0,0,0,0.28)', whiteSpace: 'pre-wrap' }}
                  action={
                    onAskChat ? (
                      <Button
                        color="inherit"
                        size="small"
                        sx={{ fontWeight: 800, textTransform: 'none', whiteSpace: 'nowrap' }}
                        onClick={() => {
                          onAskChat(formatPreviewFixPrompt(previewIssues));
                          setPreviewErr(null);
                        }}
                      >
                        Ask chat to fix
                      </Button>
                    ) : undefined
                  }
                >
                  {previewErr}
                  {previewIssues.length > 1 ? `  · ${previewIssues.length} logged` : ''}
                </Alert>
              )}
              {!previewErr && previewIssues.length > 0 && onAskChat && (
                <Alert
                  severity="warning"
                  onClose={() => setPreviewIssues([])}
                  sx={{ pointerEvents: 'auto', py: 0.25, boxShadow: '0 8px 24px rgba(0,0,0,0.28)' }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      sx={{ fontWeight: 800, textTransform: 'none' }}
                      onClick={() => {
                        onAskChat(formatPreviewFixPrompt(previewIssues));
                        setPreviewIssues([]);
                      }}
                    >
                      Ask chat to fix
                    </Button>
                  }
                >
                  {previewIssues.length} preview error{previewIssues.length === 1 ? '' : 's'} logged
                </Alert>
              )}
              {canvasHint && (
                <Alert
                  severity="warning"
                  onClose={() => onDismissCanvasHint?.()}
                  sx={{ pointerEvents: 'auto', py: 0.25, boxShadow: '0 8px 24px rgba(0,0,0,0.28)' }}
                >
                  {canvasHint}
                </Alert>
              )}
            </Stack>
          )}
        </Box>
      ) : tab === 1 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 1, overflow: 'hidden' }}>
          <Box sx={{ width: 180, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <List dense disablePadding>
              {paths.length === 0 && (
                <ListItemText
                  sx={{ px: 1.5, py: 2 }}
                  primary="No files yet"
                  primaryTypographyProps={{ variant: 'caption' }}
                />
              )}
              {paths.map((p) => (
                <ListItemButton key={p} selected={p === activePath} onClick={() => selectFile(p)}>
                  <ListItemText
                    primary={p}
                    primaryTypographyProps={{ fontSize: 12, noWrap: true }}
                    secondary={files[p]?.url ? 'image' : files[p]?.language}
                    secondaryTypographyProps={{ fontSize: 10 }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenameTo(p);
                      setActivePath(p);
                      setRenameOpen(true);
                    }}
                  >
                    <DriveFileRenameOutlineIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Delete ${p}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletePath(p);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {active?.url && !active.content ? (
              <Box component="img" src={active.url} alt={activePath} sx={{ maxWidth: '100%', borderRadius: 1 }} />
            ) : (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: PG.muted, flex: 1 }}>
                    {activePath || 'Select a file'}
                    {activePath ? ` · ${editorLanguage(activePath, active?.language) || active?.language || 'text'}` : ''}
                    {saveState ? ` · ${saveState}` : dirty ? ' · unsaved edits' : ' · saved'}
                  </Typography>
                  <Tooltip title="Write this file and reload Preview so you can see the edit.">
                    <span>
                      <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={!activePath || !sessionId}
                    onClick={saveAndRefreshPreview}
                    sx={{ ...pgBtn, fontWeight: 700, bgcolor: PG.accent, color: '#0b1220', '&:hover': { bgcolor: '#7dd3fc' } }}
                  >
                    Save
                  </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    disabled={!draft}
                    onClick={async () => {
                      await navigator.clipboard.writeText(draft);
                      setSaveState('Copied');
                      window.setTimeout(() => setSaveState(''), 1200);
                    }}
                    sx={{ textTransform: 'none', color: PG.muted }}
                  >
                    Copy file
                  </Button>
                </Stack>
                <CodeFileEditor
                  path={activePath}
                  language={active?.language}
                  value={draft}
                  onChange={setDraft}
                  onBlur={save}
                  onSave={saveAndRefreshPreview}
                />
              </>
            )}
          </Box>
        </Box>
      ) : tab === 2 ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, bgcolor: '#0a0f1a', borderRadius: 1, border: '1px solid', borderColor: PG.border }}>
          <Typography variant="subtitle2" sx={{ color: PG.text, mb: 1 }}>
            Ready to ship?
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              borderColor: 'rgba(167,139,250,0.4)',
              bgcolor: 'rgba(91,77,255,0.12)',
            }}
          >
            <Typography sx={{ fontWeight: 800, color: '#ddd6fe', mb: 0.5 }}>Build in Cursor instead</Typography>
            <Typography variant="body2" sx={{ color: PG.muted, mb: 1, lineHeight: 1.5 }}>
              Download a pack with START_HERE.md, EasyAI client docs, and attached chains so Cursor can generate the app in your repo.
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<IntegrationInstructionsIcon />}
              disabled={!sessionId || !onExportCursorPack || exportingCursorPack}
              onClick={() => setCursorOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#5b4dff', '&:hover': { bgcolor: '#6d5cff' } }}
            >
              Cursor pack
            </Button>
          </Paper>
          <Typography variant="body2" sx={{ color: PG.muted, mb: 1.5 }}>
            Python, Node, or native files show here even when they cannot run in the iframe. Ask chat for a <code>preview/index.html</code> sidecar if Preview looks empty.
          </Typography>
          <Button
            size="small"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setClearDataOpen(true)}
            disabled={!sessionId}
            sx={{ ...pgBtn, color: PG.text, mb: 2, borderColor: PG.border }}
            variant="outlined"
          >
            Clear Preview data
          </Button>
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            {[
              ['HTML preview', review.hasPreview],
              ['Input / waiting / results pages', review.hasPages],
              ['README', review.hasReadme],
              ['Hosting / START_HERE', review.hasHosting || review.hasStartHere],
              ['PASSIO_CONFIG', review.hasConfig],
            ].map(([label, ok]) => (
              <Typography key={String(label)} variant="body2" sx={{ color: ok ? PG.accent : PG.muted }}>
                {ok ? '✓' : '○'} {label}
              </Typography>
            ))}
          </Stack>
          {project.review?.message_id && (
            <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mb: 1.5 }}>
              Last builder turn {String(project.review.message_id).slice(0, 8)}
              {project.review.files_changed?.length
                ? ` · ${project.review.files_changed.length} file${project.review.files_changed.length === 1 ? '' : 's'} changed`
                : ''}
              {project.review.at ? ` · ${String(project.review.at).replace('T', ' ').slice(0, 16)}` : ''}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 700 }}>
            Tips
          </Typography>
          <ul style={{ marginTop: 4, paddingLeft: 18, color: PG.text, fontSize: 13 }}>
            {review.tips.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
          {review.sourceFiles.length > 0 && (
            <>
              <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 700 }}>
                Source files
              </Typography>
              <List dense>
                {review.sourceFiles.map((f) => (
                  <ListItemButton key={f.path} onClick={() => { selectFile(f.path); setTab(1); }}>
                    <ListItemText
                      primary={f.path}
                      secondary={`${f.language || 'file'} · ${f.lines} lines`}
                      primaryTypographyProps={{ fontSize: 12, color: PG.text }}
                      secondaryTypographyProps={{ fontSize: 10, color: PG.muted }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              width: 260,
              minHeight: 0,
              overflow: 'auto',
              bgcolor: '#0a0f1a',
              borderRadius: 1,
              p: 1.25,
              border: '1px solid',
              borderColor: PG.border,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: PG.text, mb: 0.5 }}>
              Version timeline
            </Typography>
            <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mb: 1.25 }}>
              Every chat turn, Studio step, and restore is a point you can jump back to. History survives reload. Chat itself is never rewritten.
            </Typography>
            {timeline.length === 0 && (
              <Typography variant="caption" sx={{ color: PG.muted }}>
                Empty so far. Send a builder message, Place an asset, or type on the right.
              </Typography>
            )}
            {timeline
              .slice()
              .reverse()
              .map((n) => (
                <Box key={n.key} sx={{ mb: 1.25, p: 1, bgcolor: PG.panel, borderRadius: 1, border: '1px solid', borderColor: PG.border }}>
                  <Typography variant="caption" sx={{ color: PG.accent, fontWeight: 700 }}>
                    {n.source === 'assistant'
                      ? 'From AI'
                      : n.source === 'studio'
                        ? 'Studio'
                        : n.source === 'restore'
                          ? 'Restored'
                          : 'You'}
                    {n.message_id ? ` · ${String(n.message_id).slice(0, 8)}` : ''}
                  </Typography>
                  {n.at && (
                    <Typography variant="caption" sx={{ color: PG.muted, display: 'block' }}>
                      {n.at.replace('T', ' ').slice(0, 16)}
                    </Typography>
                  )}
                  {!!n.files_changed?.length && (
                    <Typography variant="caption" sx={{ color: PG.muted, display: 'block' }}>
                      {n.files_changed.slice(0, 6).join(', ')}
                      {n.files_changed.length > 6 ? ` +${n.files_changed.length - 6}` : ''}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: PG.text, whiteSpace: 'pre-wrap', fontSize: '0.75rem', mt: 0.5 }}>
                    {(n.text || '').slice(0, 500)}
                  </Typography>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                    {n.message_id && onRestoreFromMessage && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          restoringId === n.message_id ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : (
                            <HistoryIcon fontSize="small" />
                          )
                        }
                        disabled={!!restoringId}
                        onClick={() => onRestoreFromMessage(String(n.message_id))}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          bgcolor: PG.accent,
                          color: '#0b1220',
                          '&:hover': { bgcolor: '#7dd3fc' },
                        }}
                      >
                        Restore this version
                      </Button>
                    )}
                    {n.text && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setNotes(n.text || '');
                        }}
                        sx={{ textTransform: 'none', color: PG.text, borderColor: PG.border }}
                      >
                        Load into pad
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ color: PG.muted, mb: 0.5 }}>
              Write a note and Save. It appears on the timeline at left, then this box clears so you can see it stored.
            </Typography>
            <TextField
              multiline
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void commitNote();
                }
              }}
              placeholder="Layout ideas, copy, what to change next… ⌘/Ctrl+Enter to save"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                '& .MuiInputBase-root': {
                  flex: 1,
                  alignItems: 'stretch',
                  height: '100%',
                  bgcolor: '#162033',
                  color: PG.text,
                },
                '& textarea': { fontSize: 13, lineHeight: 1.5, color: PG.text, overflow: 'auto !important' },
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
              {noteSaveState && (
                <Typography variant="caption" sx={{ color: PG.accent, fontWeight: 700 }}>
                  {noteSaveState}
                </Typography>
              )}
              <Button
                size="small"
                variant="contained"
                disabled={!notes.trim() || savingNote || !onSaveNotes}
                onClick={() => void commitNote()}
                sx={{
                  ...pgBtn,
                  fontWeight: 700,
                  bgcolor: PG.accent,
                  color: '#0b1220',
                  '&:hover': { bgcolor: '#7dd3fc' },
                }}
              >
                {savingNote ? 'Saving…' : 'Save notes'}
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      <Dialog open={placeSheetOpen} onClose={() => setPlaceSheetOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {placeHit?.role ? `Place on ${placeHit.role}` : 'Place'}
          {placeHit?.rect ? ` · ${placeHit.rect.w}×${placeHit.rect.h}px` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: PG.muted, mb: 1 }}>
            {placeHit?.tag || 'element'}
            {placeHit?.text ? ` · “${placeHit.text}”` : ''}
          </Typography>
          {placeSelector && (
            <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: PG.text }}>
              {placeSelector}
            </Typography>
          )}
          {placeHit?.styles?.background && (
            <Typography variant="caption" sx={{ display: 'block', color: PG.muted, mt: 0.5 }}>
              background {placeHit.styles.background}
              {placeHit.styles.radius ? ` · radius ${placeHit.styles.radius}` : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={() => { setPlaceSheetOpen(false); setPlaceSelector(''); }}>Cancel</Button>
          {onStudioTarget && (
            <Button
              onClick={() => {
                setPlaceSheetOpen(false);
                setMarkHits(placeHit ? [placeHit] : []);
                setMarkBox(placeHit?.rect ? { x: placeHit.rect.x, y: placeHit.rect.y, w: placeHit.rect.w, h: placeHit.rect.h } : null);
                setMarkImage(null);
                setMarkNotes('');
                setMarkOpen(true);
              }}
            >
              Describe in chat
            </Button>
          )}
          {onGenerateVideo && (
            <Button
              onClick={() => {
                setPlaceSheetOpen(false);
                setVidOpen(true);
              }}
            >
              Generate video
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => {
              setPlaceSheetOpen(false);
              setImgOpen(true);
            }}
          >
            Generate image
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={markOpen} onClose={() => setMarkOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>What should change here?</DialogTitle>
        <DialogContent>
          {markImage && (
            <Box
              component="img"
              src={markImage}
              alt="Marked region"
              sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', bgcolor: '#f8fafc', borderRadius: 1, mb: 1, border: '1px solid', borderColor: PG.border }}
            />
          )}
          <Typography variant="caption" sx={{ display: 'block', color: PG.muted, mb: 1.25, lineHeight: 1.45 }}>
            {markDead
              ? 'Preview did not return a sketch (the app may be broken). Chat still gets this region, the current page, and any logged errors — plus your notes.'
              : 'This is a layout sketch of the box you drew, not a screenshot. Chat also gets the labeled parts, the current page/file, and your notes so it knows exactly where to edit.'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: PG.muted, mb: 1 }}>
            {[
              htmlPreviewPath,
              (() => {
                const page = pages.find((p) => p.id === (pageId || pages[0]?.id));
                if (!page || page.id === 'all') return '';
                return page.kind === 'file' ? page.label : `view: ${page.label}`;
              })(),
              markBox ? `${Math.round(markBox.w)}×${Math.round(markBox.h)}px` : '',
              markHits.length ? `${markHits.length} part${markHits.length === 1 ? '' : 's'}` : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {(markHits.length ? markHits : placeHit ? [placeHit] : []).slice(0, 8).map((h, i) => (
              <Chip
                key={`${h.selector}-${i}`}
                size="small"
                label={hitChipLabel(h)}
                sx={{ bgcolor: 'rgba(56,189,248,0.12)', color: PG.text, fontWeight: 600 }}
              />
            ))}
          </Stack>
          {!markDead && !(markHits.length || placeHit) && (
            <Typography variant="caption" sx={{ display: 'block', color: PG.muted, mb: 1, lineHeight: 1.45 }}>
              No specific buttons or headings were found in that box. Chat still gets the region size — say what should appear there.
            </Typography>
          )}
          {(markHits.length ? markHits : placeHit ? [placeHit] : []).length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ bgcolor: 'transparent', '&:before': { display: 'none' }, mb: 0.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: PG.muted }} />} sx={{ minHeight: 36, px: 0 }}>
                <Typography variant="caption" sx={{ color: PG.muted }}>
                  What chat will target (selectors)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0 }}>
                {(markHits.length ? markHits : placeHit ? [placeHit] : []).slice(0, 8).map((h, i) => (
                  <Typography key={`${h.selector}-sel-${i}`} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: PG.muted }}>
                    {prettyRole(h.role, h.tag)} · {h.selector || shortSelector(h.selector)}
                    {h.rect ? ` · ${h.rect.w}×${h.rect.h}` : ''}
                  </Typography>
                ))}
              </AccordionDetails>
            </Accordion>
          )}
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={3}
            autoFocus
            label="Change notes"
            placeholder="e.g. Make this hero taller, swap the headline, use a darker photo…"
            value={markNotes}
            onChange={(e) => setMarkNotes(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                sendMarkToChat();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkOpen(false)} disabled={markSending}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!onStudioTarget || !markNotes.trim() || markSending}
            onClick={() => sendMarkToChat()}
            startIcon={markSending ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {markSending ? 'Sending…' : 'Send to chat'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imgOpen} onClose={() => setImgOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {placeHit?.role ? `Generate ${placeHit.role} image` : placeSelector ? 'Generate image here' : 'Generate image'}
        </DialogTitle>
        <DialogContent>
          {placeSelector && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: PG.muted }}>
              Target: {placeSelector}
              {placeHit?.rect ? ` · ${placeHit.rect.w}×${placeHit.rect.h}px` : ''}
              {placeHit?.role ? ` · ${placeHit.role}` : ''}
            </Typography>
          )}
          <TextField
            margin="dense"
            fullWidth
            label="Path"
            value={imgPath}
            onChange={(e) => setImgPath(e.target.value)}
          />
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={3}
            label="Prompt"
            value={imgPrompt}
            onChange={(e) => setImgPrompt(e.target.value)}
          />
          <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
            <InputLabel>Image model</InputLabel>
            <Select
              label="Image model"
              value={imageModels.some((m) => m.apiName === imgModel) ? imgModel : (imageModels[0]?.apiName || '')}
              onChange={(e) => setImgModel(String(e.target.value))}
            >
              {imageModels.map((m) => (
                <MenuItem key={m.apiName} value={m.apiName}>
                  {m.displayName} · {m.provider}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: PG.muted }}>
            Default is a strong still-image model. Only image-output models are listed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImgOpen(false); setPlaceSelector(''); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={generatingImage || !imgPrompt.trim()}
            onClick={async () => {
              const sel = placeSelector;
              const path = imgPath.trim() || 'assets/hero.png';
              const url = await onGenerateImage(path, imgPrompt.trim(), imgModel, sel || undefined);
              if (url) await placeOnHtml(url, 'image');
              if (url && onRecordStudioStep) {
                await onRecordStudioStep({
                  kind: 'image',
                  path,
                  prompt: imgPrompt.trim(),
                  selector: sel || undefined,
                  url,
                });
              }
              setImgOpen(false);
            }}
          >
            {generatingImage ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={vidOpen} onClose={() => setVidOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{placeSelector ? 'Generate video here' : 'Generate video'}</DialogTitle>
        <DialogContent>
          {placeSelector && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: PG.muted }}>
              Target: {placeSelector}
              {placeHit?.rect ? ` · ${placeHit.rect.w}×${placeHit.rect.h}px` : ''}
              {placeHit?.role ? ` · ${placeHit.role}` : ''}
            </Typography>
          )}
          <TextField
            margin="dense"
            fullWidth
            label="Path"
            value={vidPath}
            onChange={(e) => setVidPath(e.target.value)}
          />
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={3}
            label="Prompt"
            value={vidPrompt}
            onChange={(e) => setVidPrompt(e.target.value)}
            helperText="Short clip (a few seconds). This can take a minute."
          />
          <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
            <InputLabel>Video model</InputLabel>
            <Select
              label="Video model"
              value={videoModels.some((m) => m.apiName === vidModel) ? vidModel : (videoModels[0]?.apiName || '')}
              onChange={(e) => setVidModel(String(e.target.value))}
            >
              {videoModels.map((m) => (
                <MenuItem key={m.apiName} value={m.apiName}>
                  {m.displayName} · {m.provider}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: PG.muted }}>
            Default is Veo 3.1 Fast. Only video-output models are listed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setVidOpen(false); setPlaceSelector(''); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={generatingImage || !vidPrompt.trim() || !onGenerateVideo}
            onClick={async () => {
              const sel = placeSelector;
              const path = vidPath.trim() || 'assets/clip.mp4';
              const url = await onGenerateVideo?.(path, vidPrompt.trim(), vidModel, sel || undefined);
              if (url) await placeOnHtml(url, 'video');
              if (url && onRecordStudioStep) {
                await onRecordStudioStep({
                  kind: 'video',
                  path,
                  prompt: vidPrompt.trim(),
                  selector: sel || undefined,
                  url,
                });
              }
              setVidOpen(false);
            }}
          >
            {generatingImage ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setUploadBatch([]);
          setUploadNotes('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Where should these go?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: PG.muted, mb: 1.5 }}>
            Files are already in the project under assets/. Tell chat how to use them — header logo, hero, favicon, background — or keep them for later.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {uploadBatch.map((a) => (
              <Box key={a.path} sx={{ width: 88, textAlign: 'center' }}>
                {a.kind === 'video' ? (
                  <Box
                    sx={{
                      width: 88,
                      height: 88,
                      bgcolor: '#162033',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: PG.muted,
                      fontSize: 11,
                    }}
                  >
                    video
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={a.url}
                    alt={a.name}
                    sx={{ width: 88, height: 88, objectFit: 'contain', bgcolor: '#162033', borderRadius: 1 }}
                  />
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: PG.muted, wordBreak: 'break-all' }}>
                  {a.path}
                </Typography>
              </Box>
            ))}
          </Stack>
          <TextField
            multiline
            fullWidth
            minRows={2}
            value={uploadNotes}
            onChange={(e) => setUploadNotes(e.target.value)}
            placeholder="e.g. Put logo.png in the header, hero.jpg as the top banner"
          />
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setUploadOpen(false);
              setUploadBatch([]);
              setUploadNotes('');
            }}
          >
            Just keep in assets
          </Button>
          <Button variant="contained" disabled={!onStudioTarget} onClick={() => void sendUploadToChat()}>
            Send to chat
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add file</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            fullWidth
            label="Path"
            value={newPath}
            onChange={(e) => setNewPath(e.target.value)}
            helperText="e.g. styles.css, copy.md, assets/logo.svg"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const path = newPath.trim().replace(/^\/+/, '');
              if (!path || !onAddFile) return;
              await onAddFile(path, path.endsWith('.md') ? '# Notes\n' : '');
              setActivePath(path);
              setAddOpen(false);
              setTab(1);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deletePath}
        title="Delete this file?"
        body={`“${deletePath || ''}” will be removed from this project. Preview may break until you restore a version from Notes or add the file back.`}
        confirmLabel="Delete file"
        onClose={() => setDeletePath(null)}
        onConfirm={async () => {
          const path = deletePath;
          setDeletePath(null);
          if (path) await onDeleteFile(path);
        }}
      />
      <ConfirmDialog
        open={clearDataOpen}
        title="Clear Preview data?"
        body="Food logs and other localStorage this app saved in Preview will be wiped. Project files stay. Use this if a chat update changed how the app stores data and the old diary would break."
        confirmLabel="Clear data"
        onClose={() => setClearDataOpen(false)}
        onConfirm={() => {
          previewStoreRef.current = emptyPreviewStore();
          clearPreviewStore(sessionId);
          setClearDataOpen(false);
          setPreviewKey((k) => k + 1);
        }}
      />

      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Rename file</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            fullWidth
            label="New path"
            value={renameTo}
            onChange={(e) => setRenameTo(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const to = renameTo.trim().replace(/^\/+/, '');
              if (!to || !onRenameFile || to === activePath) {
                setRenameOpen(false);
                return;
              }
              await onRenameFile(activePath, to);
              setActivePath(to);
              setRenameOpen(false);
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cursorOpen}
        onClose={() => !exportingCursorPack && setCursorOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: '#12101c',
            color: PG.text,
            backgroundImage: 'linear-gradient(165deg, rgba(91,77,255,0.18), transparent 42%)',
            border: '1px solid rgba(167,139,250,0.35)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, fontWeight: 800 }}>
          Cursor pack
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: PG.muted, mt: 1, mb: 2, lineHeight: 1.55 }}>
            A folder Cursor can open and build from — the same docs Studio uses, plus every chain, function, and object attached to this chat. Works even before you generate an app here.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
              mb: 2,
            }}
          >
            {[
              ['START_HERE.md', 'Entrypoint — tell Cursor to read this first'],
              ['AGENTS.md + Cursor rule', 'How to call EasyAI; do not recreate chains'],
              ['Attached chains', (chainNames || []).length ? (chainNames || []).join(', ') : 'Whatever you attach in Settings'],
              ['Current app files', paths.length ? `${paths.length} files under app/` : 'Omitted until Studio has files'],
            ].map(([title, hint]) => (
              <Paper
                key={String(title)}
                variant="outlined"
                sx={{ p: 1.25, bgcolor: 'rgba(15,12,28,0.6)', borderColor: 'rgba(167,139,250,0.28)' }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#ddd6fe' }}>{title}</Typography>
                <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mt: 0.4, lineHeight: 1.45 }}>
                  {hint}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: PG.muted, display: 'block', lineHeight: 1.5 }}>
            Unzip → Cursor <strong>File → Open Folder</strong> → keep START_HERE.md in context. Paste your EasyAI API key only in config placeholders.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, justifyContent: 'space-between' }}>
          <Button
            startIcon={<ContentCopyIcon />}
            disabled={!onCopyCursorBrief}
            onClick={() => onCopyCursorBrief?.()}
            sx={{ textTransform: 'none', color: PG.muted }}
          >
            Copy one-liner
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setCursorOpen(false)} sx={{ textTransform: 'none', color: PG.muted }}>
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={exportingCursorPack ? <CircularProgress size={14} color="inherit" /> : <IntegrationInstructionsIcon />}
              disabled={!sessionId || !onExportCursorPack || exportingCursorPack}
              onClick={async () => {
                try {
                  await onExportCursorPack?.();
                  setCursorOpen(false);
                } catch {
                  /* error shown in Playground */
                }
              }}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#5b4dff', '&:hover': { bgcolor: '#6d5cff' } }}
            >
              {exportingCursorPack ? 'Packing…' : 'Download pack'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={exportOpen} onClose={() => !exportingPack && setExportOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>Export for hosting</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: PG.muted, mb: 2, mt: 0.5 }}>
            We write README, HOSTING.md, start scripts, and the config files for your target, then download a zip. Pick where the app should live — buckets, Cloud Run, Netlify, or a Python host. “You pick” includes every recipe.
          </Typography>
          {HOST_GROUPS.map((group) => (
            <Box key={group.label} sx={{ mb: 1.75 }}>
              <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 700, letterSpacing: '0.04em', display: 'block', mb: 0.75 }}>
                {group.label.toUpperCase()}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1,
                }}
              >
                {group.options.map((h) => {
                  const selected = hostTarget === h.id;
                  return (
                    <Paper
                      key={h.id}
                      variant="outlined"
                      onClick={() => setHostTarget(h.id)}
                      sx={{
                        p: 1.25,
                        cursor: 'pointer',
                        borderColor: selected ? PG.accent : PG.border,
                        bgcolor: selected ? 'rgba(56,189,248,0.12)' : PG.chat,
                        '&:hover': { borderColor: PG.accent },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: PG.text }}>
                        {h.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mt: 0.35, lineHeight: 1.4 }}>
                        {h.hint}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          ))}
          {exportingPack && (
            <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 0.5 }}>
              Writing README, start scripts, hosting notes, and deploy files…
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExportOpen(false)} disabled={exportingPack} sx={pgBtn}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={exportingPack || !onExportPack}
            startIcon={exportingPack ? <CircularProgress size={14} color="inherit" /> : <IosShareIcon />}
            onClick={() => {
              onExportPack?.(hostTarget);
              setExportOpen(false);
            }}
            sx={{ ...pgBtn, fontWeight: 700 }}
          >
            {exportingPack ? 'Exporting…' : 'Write pack & download zip'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export type { ProjectFile, ProjectState };
